import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import {
  Box,
  Button,
  Divider,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import moment from 'moment'
import API from '../../../../services/API'
import { GlobalContext, UserContext } from '../../../../App'
import AllowanceProgressLine from '../../../../components/Subscriptions/AllowanceProgressLine'
import SubscriptionAgreementDialog, {
  subscriptionAgreementPath,
  useSubscriptionAgreementStatus,
} from '../../../../legal/SubscriptionAgreementDialog'

const AGREEMENT_NAME = 'AmbuNet Business Subscription Agreement'

const PTS_METRIC = 'ptsJourneysPerMonth'
const CONTROL_ROOM_HOURS_METRIC = 'controlRoomHoursPerMonth'

// The same calm ambers as AllowanceProgressLine and BillingWarning.
const AMBER_TEXT = '#92400E'
const AMBER_BORDER = 'rgba(230, 126, 34, 0.28)'
const AMBER_BACKGROUND = '#FFF7ED'

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value)

const formatFigure = (value) =>
  Number(isNumber(value) ? value : 0).toLocaleString('en-GB', {
    maximumFractionDigits: 1,
  })

// 'YYYY-MM' → "September 2026" / "Sep 2026".
const formatPeriod = (period, format) => {
  const month = moment(period, 'YYYY-MM', true)
  return month.isValid() ? month.format(format) : (period ?? '')
}

// Why this month's usage is not being reviewed against the allowances, by the
// server's `notMonitoredReason`. 'notRequired' (the demo, or no plan) says
// nothing at all; any other reason gets the plain fallback.
const NOT_MONITORED_NOTES = {
  noAgreement: `These allowances apply once your organisation has accepted the ${AGREEMENT_NAME}. Until then, usage is shown for information only.`,
  planChangedSinceAcceptance:
    'Your plan has changed since the agreement was accepted. The allowances for your new package apply once its Plan Summary has been accepted. Until then, usage is shown for information only.',
  beforeMonitoringStart:
    'These allowances apply from the first full calendar month after the agreement was accepted. Until then, usage is shown for information only.',
}
const NOT_MONITORED_FALLBACK = 'Usage this month is shown for information only.'

// What is waiting to be reviewed, by the agreement's `reason`.
const PENDING_COPY = {
  planChanged:
    'Your plan has changed since the agreement was accepted, so there is an updated Plan Summary for your organisation to review. Nothing about your access changes in the meantime.',
  newVersion:
    'AmbuNet has published an updated version of the agreement for your organisation to review. Nothing about your access changes in the meantime.',
}
const PENDING_FALLBACK = `Your organisation has not yet accepted the ${AGREEMENT_NAME}, which sets out the allowances in your plan. Nothing about your access changes in the meantime.`

// "200 included with your plan, plus 10 purchased and 5 complimentary." Only
// said when there are seats on top of the plan's own.
const seatsCaption = ({ included, purchasedSeats, complimentarySeats }) => {
  const extras = []
  if (purchasedSeats > 0) {
    extras.push(`${formatFigure(purchasedSeats)} purchased`)
  }
  if (complimentarySeats > 0) {
    extras.push(`${formatFigure(complimentarySeats)} complimentary`)
  }
  if (!extras.length || !isNumber(included)) return undefined
  return `${formatFigure(included)} included with your plan, plus ${extras.join(' and ')}.`
}

const cardSx = { p: { xs: 2, sm: 3 }, mb: 4, border: 'solid 1px' }

const SectionHeading = ({ title, children }) => (
  <Box>
    <Typography variant="h6">{title}</Typography>
    {children && (
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    )}
  </Box>
)

// As many lines side by side as there is room for (three on a desktop),
// stacked on a phone. Sized by the card, not the window, because the page
// shares the window with the navigation.
const LineGrid = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      columnGap: 4,
      rowGap: 2.5,
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))',
    }}
  >
    {children}
  </Box>
)

// A quiet panel with an information icon. `amber` is for the server's
// above-allowance sentence: calm, and deliberately not an error or a warning
// triangle.
const Note = ({ amber = false, children }) => (
  <Box
    role="status"
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      px: 2,
      py: 1.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: amber ? AMBER_BORDER : 'divider',
      bgcolor: amber ? AMBER_BACKGROUND : 'background.default',
    }}
  >
    <InfoOutlinedIcon
      fontSize="small"
      sx={{
        flexShrink: 0,
        mt: '1px',
        color: amber ? AMBER_TEXT : 'text.secondary',
      }}
    />
    <Typography
      variant="body2"
      sx={{ color: amber ? '#4B5563' : 'text.secondary', lineHeight: 1.5 }}
    >
      {children}
    </Typography>
  </Box>
)

// One month's figure in the history table. The allowance is only shown for a
// month that was being reviewed against it, and the amber marker only where
// the server lists the metric as above its allowance.
const HistoryFigure = ({ used, allowance, above }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      whiteSpace: 'nowrap',
    }}
  >
    <span>
      {formatFigure(used)}
      {isNumber(allowance) && (
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {' '}
          / {formatFigure(allowance)}
        </Box>
      )}
    </span>
    {above && (
      <Box
        component="span"
        sx={{
          px: 0.75,
          borderRadius: 1,
          fontSize: '0.75rem',
          fontWeight: 600,
          lineHeight: 1.6,
          color: AMBER_TEXT,
          bgcolor: AMBER_BACKGROUND,
          border: `1px solid ${AMBER_BORDER}`,
        }}
      >
        Above allowance
      </Box>
    )}
  </Box>
)

// The previous 11 calendar months, newest first (the server sends them
// oldest first). Wider than a phone, so it scrolls sideways inside its own
// container and the page never does.
const HistoryTable = ({ rows }) => (
  <TableContainer
    sx={{
      maxWidth: '100%',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
    }}
  >
    <Table
      size="small"
      aria-label="Usage in the previous 11 calendar months"
      sx={{ minWidth: 560, '& th': { whiteSpace: 'nowrap' } }}
    >
      <TableHead>
        <TableRow>
          <TableCell>Month</TableCell>
          <TableCell>PTS journeys</TableCell>
          <TableCell>Control-room hours</TableCell>
          <TableCell>Peak concurrent rooms</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...rows].reverse().map((row) => {
          const above = Array.isArray(row.aboveAllowance)
            ? row.aboveAllowance
            : []
          const reviewed = row.monitored === true
          return (
            <TableRow key={row.period}>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {formatPeriod(row.period, 'MMM YYYY')}
              </TableCell>
              <TableCell>
                <HistoryFigure
                  used={row.ptsJourneys}
                  allowance={reviewed ? row.ptsAllowance : null}
                  above={above.includes(PTS_METRIC)}
                />
              </TableCell>
              <TableCell>
                <HistoryFigure
                  used={row.controlRoomHours}
                  allowance={reviewed ? row.controlRoomAllowanceHours : null}
                  above={above.includes(CONTROL_ROOM_HOURS_METRIC)}
                />
              </TableCell>
              <TableCell>{formatFigure(row.peakConcurrentRooms)}</TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  </TableContainer>
)

// The agreement the allowances come from: the version that was accepted, a
// link to read it and, while something is waiting, the way to review it.
const AgreementBlock = ({ agreement, onReview }) => {
  const { status, reason, acceptedVersion, acceptedAt, acceptedByName } =
    agreement
  const acceptedOn = acceptedAt
    ? moment(acceptedAt).format('D MMMM YYYY')
    : null

  return (
    <Stack gap={1.5}>
      <SectionHeading title="Business Subscription Agreement" />
      {acceptedVersion && (
        <Box>
          <Typography variant="body1">
            Version {acceptedVersion} accepted
            {acceptedOn ? ` on ${acceptedOn}` : ''}
            {acceptedByName ? ` by ${acceptedByName}` : ''}.
          </Typography>
          <Link
            href={subscriptionAgreementPath(acceptedVersion)}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            Read the agreement (version {acceptedVersion})
            <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
          </Link>
        </Box>
      )}
      {status === 'pending' && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={1.5}
        >
          <Typography variant="body2" color="text.secondary">
            {PENDING_COPY[reason] ?? PENDING_FALLBACK}
          </Typography>
          <Button
            variant="contained"
            onClick={onReview}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Review agreement
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

const UsagePanel = () => {
  const { tenantInfo } = useContext(GlobalContext)
  const [usage, setUsage] = useState(null)
  // 'loading' | 'ready' | 'failed' | 'hidden'
  const [phase, setPhase] = useState('loading')
  const [reviewWanted, setReviewWanted] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const mounted = useRef(true)
  const shownAgreement = useRef(null)

  // Switched on by the first click on "Review agreement", never before: while
  // an agreement is pending each read costs the server a call to the payment
  // provider.
  const {
    agreementStatus,
    loading: agreementLoading,
    refresh,
    applyStatus,
  } = useSubscriptionAgreementStatus(reviewWanted && !tenantInfo?.isDemo)

  // skipGlobalErrorHandler: every outcome is dealt with here. Left to the
  // global handler a refusal would never settle this promise, and a server
  // error would replace the whole Billing page. `quiet` refreshes what is
  // already on screen and leaves it alone if the read fails.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setPhase('loading')
    let outcome = 'failed'
    try {
      const res = await API.get('/billing/usage-allowances', {
        skipGlobalErrorHandler: true,
      })
      if (!mounted.current) return
      if (res.status === 200 && res.data?.period) {
        shownAgreement.current = res.data.agreement ?? null
        setUsage(res.data)
        setPhase('ready')
        return
      }
      // Not for this user after all, or no organisation to report on: there
      // is nothing to show and nothing to retry.
      if (res.status === 403 || res.status === 404) outcome = 'hidden'
    } catch (e) {
      // Offline or timed out: the same as any other failed read.
    }
    if (mounted.current && !quiet) setPhase(outcome)
  }, [])

  useEffect(() => {
    mounted.current = true
    load()
    return () => {
      mounted.current = false
    }
  }, [load])

  // The agreement was accepted (here or through the site-wide reminder), so
  // what this month is reviewed against has changed too.
  useEffect(() => {
    const shown = shownAgreement.current
    if (!agreementStatus || !shown) return
    if (
      agreementStatus.status !== shown.status ||
      (agreementStatus.acceptedVersion ?? null) !==
        (shown.acceptedVersion ?? null)
    ) {
      load({ quiet: true })
    }
  }, [agreementStatus, load])

  if (phase === 'hidden') return null

  if (phase !== 'ready' || !usage) {
    return (
      <Stack component={Paper} sx={cardSx} gap={1}>
        <Typography variant="h5">Usage and Allowances</Typography>
        {phase === 'failed' ? (
          <Stack
            direction="row"
            flexWrap="wrap"
            alignItems="center"
            gap={1.5}
            role="status"
          >
            <Typography variant="body2" color="text.secondary">
              Your usage could not be loaded just now.
            </Typography>
            <Button size="small" variant="outlined" onClick={() => load()}>
              Try again
            </Button>
          </Stack>
        ) : (
          <Box aria-busy="true">
            <Skeleton width="45%" />
            <Skeleton />
            <Skeleton width="70%" />
          </Box>
        )}
      </Stack>
    )
  }

  const hardLimits = usage.hardLimits ?? {}
  const employees = hardLimits.employees ?? {}
  const vehicles = hardLimits.vehicles ?? {}
  const baseLocations = hardLimits.baseLocations ?? {}
  const pts = usage.softThresholds?.[PTS_METRIC] ?? {}
  const hours = usage.softThresholds?.[CONTROL_ROOM_HOURS_METRIC] ?? {}
  const rooms = usage.concurrentControlRooms ?? {}
  const history = Array.isArray(usage.history) ? usage.history : []

  // Only a month the server says is monitored can ever be "above": anything
  // else is drawn as information, whatever the figures are.
  const monitored = usage.monitored === true
  const softVariant = monitored ? 'soft' : 'info'
  const hasSoftAllowances =
    isNumber(pts.allowance) || isNumber(hours.allowanceHours)
  const notMonitoredNote =
    monitored ||
    usage.notMonitoredReason === 'notRequired' ||
    !hasSoftAllowances
      ? null
      : (NOT_MONITORED_NOTES[usage.notMonitoredReason] ??
        NOT_MONITORED_FALLBACK)

  // Nothing to tabulate for a plan without these allowances that has never
  // used PTS or a control room.
  const showHistory = history.some(
    (row) =>
      row.ptsJourneys > 0 ||
      row.controlRoomHours > 0 ||
      row.peakConcurrentRooms > 0 ||
      isNumber(row.ptsAllowance) ||
      isNumber(row.controlRoomAllowanceHours),
  )

  // Only a month that was reviewed against an allowance shows one.
  const historyShowsAllowances = history.some(
    (row) =>
      row.monitored === true &&
      (isNumber(row.ptsAllowance) || isNumber(row.controlRoomAllowanceHours)),
  )

  // What the review dialog's own read says wins, so the block updates the
  // moment the agreement is accepted.
  const agreement = {
    ...(usage.agreement ?? {}),
    ...(agreementStatus && {
      status: agreementStatus.status,
      reason: agreementStatus.reason,
      acceptedVersion: agreementStatus.acceptedVersion,
      acceptedAt: agreementStatus.acceptedAt,
      acceptedByName: agreementStatus.acceptedByName,
    }),
  }
  const showAgreement =
    !tenantInfo?.isDemo &&
    (agreement.status === 'accepted' || agreement.status === 'pending')

  // Concurrent control rooms are enforced capacity, like employees. A plan
  // that includes none (Core, Premium) only shows the line if rooms have
  // actually been open, so it never reads as "no limit" on a module the plan
  // does not have.
  const hasRoomsAllowance = isNumber(rooms.allowance) && rooms.allowance > 0
  const showRoomsLine =
    hasRoomsAllowance || rooms.current > 0 || rooms.peakThisMonth > 0

  return (
    <>
      <Stack component={Paper} sx={cardSx} gap={3}>
        <Box>
          <Typography variant="h5">Usage and Allowances</Typography>
          <Typography variant="body2" color="text.secondary">
            What your organisation is using of the allowances in its plan.
          </Typography>
        </Box>

        <Stack gap={1.5}>
          <SectionHeading title="Capacity">
            Employee, vehicle, base-location and concurrent control-room
            capacity may require an agreed upgrade before further records are
            added or further control rooms are opened.
          </SectionHeading>
          <LineGrid>
            <AllowanceProgressLine
              label="Employees"
              used={employees.used}
              allowance={employees.limit}
              unit="employees"
              variant="hard"
              caption={seatsCaption(employees)}
            />
            <AllowanceProgressLine
              label="Vehicles"
              used={vehicles.used}
              allowance={vehicles.limit}
              unit="vehicles"
              variant="hard"
            />
            <AllowanceProgressLine
              label="Base locations"
              used={baseLocations.used}
              allowance={baseLocations.limit}
              unit="base locations"
              variant="hard"
            />
            {showRoomsLine && (
              <AllowanceProgressLine
                label="Concurrent control rooms"
                used={rooms.current}
                allowance={rooms.allowance}
                variant="hard"
                usedLabel={
                  hasRoomsAllowance
                    ? `${formatFigure(rooms.current)} of ${formatFigure(rooms.allowance)} open now`
                    : `${formatFigure(rooms.current)} open now`
                }
                caption={
                  isNumber(rooms.peakThisMonth)
                    ? `Peak this month: ${formatFigure(rooms.peakThisMonth)} open at once. Rooms already open are never closed or interrupted.`
                    : undefined
                }
              />
            )}
          </LineGrid>
        </Stack>

        <Divider />

        <Stack gap={1.5}>
          <SectionHeading
            title={`This Month (${formatPeriod(usage.period, 'MMMM YYYY')})`}
          >
            {monitored &&
              hasSoftAllowances &&
              (hours.enforced === true
                ? 'PTS journeys are recorded for review only — they never interrupt operational access or generate overage charges.'
                : 'PTS journeys and control-room hours are recorded for review only — they never interrupt operational access or generate overage charges.')}
          </SectionHeading>
          {notMonitoredNote && <Note>{notMonitoredNote}</Note>}
          <LineGrid>
            <AllowanceProgressLine
              label="PTS journeys"
              used={pts.used}
              allowance={pts.allowance}
              unit="journeys"
              variant={softVariant}
              aboveAllowance={monitored && pts.aboveAllowance === true}
            />
            {hours.enforced === true ? (
              <AllowanceProgressLine
                label="Control-room hours"
                used={hours.usedHours}
                allowance={hours.allowanceHours}
                unit="hours"
                variant="hard"
                caption="A monthly limit on your plan: you're warned when 10 hours remain, and open control rooms close when the hours run out."
              />
            ) : (
              <AllowanceProgressLine
                label="Control-room hours"
                used={hours.usedHours}
                allowance={hours.allowanceHours}
                unit="hours"
                variant={softVariant}
                aboveAllowance={monitored && hours.aboveAllowance === true}
              />
            )}
          </LineGrid>
          {/* The server's sentence, exactly as sent, and only when it sends
              one. Never worded here. */}
          {typeof usage.softThresholdNotice === 'string' &&
            usage.softThresholdNotice && (
              <Note amber>{usage.softThresholdNotice}</Note>
            )}
        </Stack>

        {showHistory && (
          <>
            <Divider />
            <Stack gap={1.5}>
              <SectionHeading title="Previous 11 Months">
                Usage in each calendar month, newest first.
                {historyShowsAllowances &&
                  ' Figures are shown as used / allowance for the months your allowances applied to.'}
              </SectionHeading>
              <HistoryTable rows={history} />
            </Stack>
          </>
        )}

        {showAgreement && (
          <>
            <Divider />
            <AgreementBlock
              agreement={agreement}
              onReview={() => {
                setReviewWanted(true)
                setReviewOpen(true)
              }}
            />
          </>
        )}
      </Stack>
      {/* A sibling of the card, never inside it: a MUI portal still bubbles
          React events to its React ancestors. */}
      <SubscriptionAgreementDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        agreementStatus={agreementStatus}
        loading={agreementLoading}
        onRefresh={refresh}
        onStatusChange={applyStatus}
      />
    </>
  )
}

// Usage against the plan's allowances, for the Billing page: capacity
// (employees, vehicles, base locations, concurrently open control rooms),
// this month's PTS journeys and control-room hours, the previous 11 months,
// and the agreement the allowances come from.
//
// For the tenant owner and site administrators only. The standalone /billing
// route is open to any signed-in user and the endpoint refuses everyone else,
// so for anyone else this renders nothing and asks the server for nothing.
//
// Everything shown is the server's own verdict: a month is only ever "above
// allowance" where GET /billing/usage-allowances says so, and the sentence
// explaining it is the server's, shown as sent. PTS journeys and control-room
// hours are recorded for review only, so above one is calm amber and never an
// error; concurrent control rooms are enforced capacity (a hard line, like
// employees) and are never an "above allowance" month.
//
// The boundary keeps a failure in here from taking the Billing page with it.
const UsageAllowances = () => {
  const { user } = useContext(UserContext)
  const mayView =
    user?.security?.isTenantOwner === true ||
    user?.security?.isSiteAdmin === true

  if (!mayView) return null

  return (
    <ErrorBoundary fallbackRender={() => null} onError={console.error}>
      <UsagePanel />
    </ErrorBoundary>
  )
}

export default UsageAllowances
